/*
 * A native `<select>`, restyled, with the chevron drawn to match the field.
 *
 * Native rather than a custom listbox, and the reason is not effort. A custom
 * listbox has to reimplement type-ahead, Home and End, the typed-prefix search
 * that lets somebody find "Zamfara" in a list of thirty-seven states by typing
 * "za", and the platform picker that Android and iOS put up instead of a
 * dropdown. Almost every hand-built one ships without the first three, so a
 * keyboard user goes from one keystroke to thirty-six arrow presses.
 *
 * What is given up is the ability to style the option list. That is a real
 * cost, and the console pays it: an option list is a list of words, and the
 * closed field, which is the part that is on screen ninety-nine per cent of the
 * time, matches every other control exactly.
 */

import type { ReactNode, SelectHTMLAttributes } from 'react'
import { joinIds } from '../aria'
import { useFieldControl } from '../Field/fieldContext'
import { ChevronMark } from '../marks'
import styles from './Select.module.css'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: ReadonlyArray<SelectOption>
  /**
   * The empty first entry. Given a value of '' and disabled, so it can be shown
   * as the initial state and cannot be chosen deliberately: "Select a project"
   * is not a project.
   */
  placeholder?: string
  children?: ReactNode
}

export function Select({ options, placeholder, className, children, ...rest }: SelectProps) {
  const field = useFieldControl()

  const selectClassName = [styles.select, className].filter(Boolean).join(' ')

  return (
    <span className={styles.wrap}>
      <select
        {...rest}
        id={rest.id ?? field?.id}
        className={selectClassName}
        aria-describedby={joinIds(rest['aria-describedby'], field?.describedBy)}
        aria-invalid={rest['aria-invalid'] ?? (field?.invalid ? true : undefined)}
        aria-required={rest['aria-required'] ?? (field?.required ? true : undefined)}
      >
        {placeholder === undefined ? null : (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronMark className={styles.chevron} />
    </span>
  )
}
